import React from 'react';
import { Box, Heading, List, ListItem, Text } from '@chakra-ui/react';

/**
 * How to read the review queue, computed from the queue itself.
 *
 * The engine ranks clients inside this book (urgency policy relative_to_book):
 * High = top 5%, Medium = next 15% of the clients whose evidence is recent
 * enough and whose score is at least 1. These are review-order labels, not an
 * absolute risk or a probability; change over time needs two dated snapshots.
 */
export const summarizeQueue = (items = []) => {
  const basis = items.find((item) => item?.breakdown?.urgency_basis)?.breakdown.urgency_basis || null;
  const withHistory = items.filter((item) => Number(item?.history_snapshots) >= 2).length;
  const historyKnown = items.some((item) => item?.history_snapshots !== undefined && item?.history_snapshots !== null);
  return {
    total: items.length,
    method: basis?.method || null,
    highPct: basis?.high_top_pct ?? null,
    mediumPct: basis?.medium_top_pct ?? null,
    eligible: basis?.eligible ?? null,
    bookSize: basis?.book_size ?? null,
    minScore: basis?.min_priority_score ?? null,
    withoutHistory: historyKnown ? items.length - withHistory : null,
  };
};

const QueueReadingGuide = ({ items }) => {
  const summary = summarizeQueue(items);
  if (!summary.total) return null;
  const relative = summary.method === 'relative_to_book';
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={{ base: 4, md: 5 }} data-testid="queue-reading-guide">
      <Heading size="sm" color="brand.navy" mb={2}>How to read this list</Heading>
      <List spacing={1.5} fontSize="sm" color="gray.700">
        <ListItem>
          <b>Order.</b> Clients are sorted by priority score within this book: a comparison between your own clients, based on the evidence your data contains.
        </ListItem>
        {relative ? (
          <ListItem>
            <b>High and Medium</b> mark the top {summary.highPct}% and the next {summary.mediumPct - summary.highPct}% of the {summary.eligible?.toLocaleString('en-US')} clients
            {' '}whose evidence is recent enough and whose score is at least {summary.minScore}. They say where to start reviewing, not how risky a client is, and they are not a probability.
          </ListItem>
        ) : null}
        <ListItem>
          <b>Low</b> is every other client, including those whose evidence is too old or too thin to rank.
        </ListItem>
        {summary.withoutHistory !== null ? (
          <ListItem>
            <b>Change over time.</b>{' '}
            {summary.withoutHistory === summary.total
              ? 'This is the first dated snapshot of your book, so changes are not measured yet. They appear after your next dated upload.'
              : `${summary.withoutHistory.toLocaleString('en-US')} of ${summary.total.toLocaleString('en-US')} clients have a single dated snapshot; for them change over time is not measured yet.`}
          </ListItem>
        ) : null}
      </List>
      <Text fontSize="xs" color="gray.500" mt={2}>Missing data stays missing: it never counts as a zero or as an observed signal.</Text>
    </Box>
  );
};

export default QueueReadingGuide;
